import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { STORE_JOB_ROLES, STORE_STAFF_JOB_ROLES_BY_STORE, TIP_POOLS } from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { JobRolesTab } from "src/features/Payroll/components/JobRolesTab";
import { EmployeeJobRolesTab } from "src/features/Payroll/components/EmployeeJobRolesTab";
import { CompensationTab } from "src/features/Payroll/components/CompensationTab";
import { TipPoolsTab } from "src/features/Payroll/components/TipPoolsTab";

const PAYROLL_TABS = [
  { id: "jobRoles", label: "Job Roles" },
  { id: "employeeRoles", label: "Employee Job Roles" },
  { id: "compensation", label: "Compensation" },
  { id: "tipPools", label: "Tip Pools" },
];

export function PayrollAndTipsSettings() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [activeTab, setActiveTab] = useState("jobRoles");
  const [stats, setStats] = useState<{ jobRoles: number; employeesAssigned: number; activeTipPools: number }>({
    jobRoles: 0,
    employeesAssigned: 0,
    activeTipPools: 0,
  });

  const fetchStats = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const [rolesRes, assignRes, poolsRes] = await Promise.all([
        axBe.post<{ data?: { storeJobRoles?: unknown[] } }>(GRAPHQL_ENDPOINT, {
          query: STORE_JOB_ROLES,
          variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: false },
        }),
        axBe.post<{ data?: { storeStaffJobRolesByStore?: { storeStaffId: number }[] } }>(GRAPHQL_ENDPOINT, {
          query: STORE_STAFF_JOB_ROLES_BY_STORE,
          variables: { storeCorrelationId: hasRestaurant.correlationId },
        }),
        axBe.post<{ data?: { tipPools?: unknown[] } }>(GRAPHQL_ENDPOINT, {
          query: TIP_POOLS,
          variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: true },
        }),
      ]);
      const roles = rolesRes.data?.data?.storeJobRoles ?? [];
      const assignments = assignRes.data?.data?.storeStaffJobRolesByStore ?? [];
      const pools = poolsRes.data?.data?.tipPools ?? [];
      const distinctStaff = new Set(assignments.map((a) => a.storeStaffId)).size;
      setStats({ jobRoles: roles.length, employeesAssigned: distinctStaff, activeTipPools: pools.length });
    } catch {
      setStats({ jobRoles: 0, employeesAssigned: 0, activeTipPools: 0 });
    }
  }, [hasRestaurant?.correlationId, axBe]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="w-full min-w-0 max-w-full px-4 sm:px-6">
      <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Payroll & Tips</h2>
      <div className="border-b border-[#e5e7eb] pb-4 mb-5">
        <p className="text-[14px] text-[#6b7280] mb-2">
          Configure employee roles, pay structure, and tip distribution.
        </p>
        <p className="text-xs text-[#9ca3af] mb-4">
          Setup order: Create roles → Assign employees → Configure compensation → Create tip pool
        </p>
        <div className="flex flex-wrap gap-3">
          <span className="inline-flex items-center rounded-lg bg-[#f3f4f6] px-3 py-1.5 text-sm font-medium text-[#374151]">
            Job Roles: {stats.jobRoles}
          </span>
          <span className="inline-flex items-center rounded-lg bg-[#f3f4f6] px-3 py-1.5 text-sm font-medium text-[#374151]">
            Employees Assigned: {stats.employeesAssigned}
          </span>
          <span className="inline-flex items-center rounded-lg bg-[#f3f4f6] px-3 py-1.5 text-sm font-medium text-[#374151]">
            Active Tip Pools: {stats.activeTipPools}
          </span>
        </div>
      </div>
      <div className="border-b border-[#e5e7eb] mb-4">
        <div className="flex gap-x-6 items-center">
          {PAYROLL_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`h-12 flex items-center cursor-pointer relative transition-colors ${
                activeTab === tab.id
                  ? "text-[#FD7657] border-b-2 border-[#FD7657] -mb-px font-semibold"
                  : "text-[#6b7280] border-b-0 hover:text-[#374151] font-normal"
              }`}
            >
              <span className="text-sm">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      {activeTab === "jobRoles" && (
        <JobRolesTab
          onUpdate={fetchStats}
          onNavigateToTipPools={() => setActiveTab("tipPools")}
          onNavigateToEmployeeRoles={() => setActiveTab("employeeRoles")}
        />
      )}
      {activeTab === "employeeRoles" && <EmployeeJobRolesTab />}
      {activeTab === "compensation" && <CompensationTab />}
      {activeTab === "tipPools" && <TipPoolsTab />}
    </div>
  );
}
